import { makeStyles } from '@material-ui/styles'
import TextField from 'core/components/validatedForm/TextField'
import PicklistField from 'core/components/validatedForm/DropdownField'
import DateTimePickerField from 'core/components/validatedForm/DateTimePickerField'
import RemoveButton from './RemoveButton'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import React, { useCallback, useMemo, useState } from 'react'
import useReactRouter from 'use-react-router'
import { createApplicationCredential } from './actions'
import ValidatedRolesMultiPicklist, { RoleOption } from './RolesMultiPicklist'

import ServicePicklist from './ServicePicklist'
import SuccessModal from './SuccessModal'
import ValidatedMethodsMultiPicklist from './MethodsMultiPicklist'
import { AccessRuleMethod, ApplicationCredential } from './model'
import { EMPTY_ACCESS_RULE } from './constant'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Combobox from 'core/elements/combobox/Combobox'
import { getPathsForService, ApiPathConfig } from './api-access-rules-config'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
  },
  dropdownWrapper: {
    marginTop: 0,
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  accessRulesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  accessRuleRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.5fr 1fr 40px',
    gap: theme.spacing(2),
    alignItems: 'start',
  },
  textFieldWrapper: {
    '& [class*="validatedFormInput"] .inputFrame': {
      marginTop: '3.5px',
    },
  },
  methodsWrapper: {
    '& .inputFrame': {
      marginTop: '11.5px',
    },
  },
  removeButtonWrapper: {
    paddingTop: 36,
  },
  iconButton: {
    cursor: 'pointer',
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: theme.components.button.primary.background,
    color: theme.components.button.primary.color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  icon: {
    color: theme.components.button.primary.color,
    fontSize: 12,
  },
  addButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    color: theme.palette.primary.main,
    marginTop: theme.spacing(2),
    width: '150px',
  },
  restrictedContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}))

interface AccessRuleInput {
  service: string
  path: string
  methods: AccessRuleMethod[]
}

interface Props {
  addRoute: Route
  onSuccess?: () => void
}

export default function CreateApplicationCredentialModal({ addRoute, onSuccess }: Props) {
  const classes = useStyles()
  const { history } = useReactRouter()
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [createdCredential, setCreatedCredential] = useState<ApplicationCredential | null>(null)
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState(false)
  const defaultParams = {
    name: '',
    description: '',
    expirationDate: '',
    roles: [] as RoleOption[],
    restricted: true,
    accessRules: [{ ...EMPTY_ACCESS_RULE }] as AccessRuleInput[],
  }

  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)
  const { update, updating, error, reset } = useUpdateAction(createApplicationCredential)
  const handleAddAccessRule = useCallback(() => {
    updateParams({
      accessRules: [...params.accessRules, { ...EMPTY_ACCESS_RULE }],
    })
  }, [params.accessRules, updateParams])

  const handleRemoveAccessRule = useCallback(
    (index: number) => {
      // Prevent removing the first access rule (index 0) as it's compulsory
      if (index === 0) return
      const newRules = params.accessRules.filter((_, i) => i !== index)
      updateParams({ accessRules: newRules })
    },
    [params.accessRules, updateParams],
  )

  const handleAccessRuleChange = useCallback(
    (index: number, field: keyof AccessRuleInput, value: string | AccessRuleMethod[]) => {
      const newRules = [...params.accessRules]
      newRules[index] = { ...newRules[index], [field]: value }

      // Clear path and methods when service changes
      if (field === 'service') {
        newRules[index].path = ''
        newRules[index].methods = []
      }

      updateParams({ accessRules: newRules })
    },
    [params.accessRules, updateParams],
  )

  // Get available paths for a selected service (wraps imported function)
  const getPathsForServiceMemo = useCallback((service: string): ApiPathConfig[] => {
    if (!service) return []
    return getPathsForService(service)
  }, [])

  // Check if an access rule row is partially filled (at least one field has value)
  const isAccessRulePartiallyFilled = useCallback((rule: AccessRuleInput) => {
    return Boolean(rule.service || rule.path || rule.methods.length > 0)
  }, [])

  // Get validation error for a specific field in an access rule row
  const getAccessRuleFieldError = useCallback(
    (rule: AccessRuleInput, field: keyof AccessRuleInput) => {
      // Only show errors after form has been submitted
      if (!hasFormBeenSubmitted) return undefined

      const isMethodsField = field === 'methods'
      const hasValue = isMethodsField ? rule.methods.length > 0 : Boolean(rule[field])

      // Only show error if row is partially filled
      if (!isAccessRulePartiallyFilled(rule)) return undefined
      if (!hasValue) {
        return 'Field is required'
      }

      return undefined
    },
    [isAccessRulePartiallyFilled, hasFormBeenSubmitted],
  )

  // Check if there are any incomplete access rules
  const hasIncompleteAccessRules = useMemo(() => {
    return params.accessRules.some((rule) => {
      // Only validate if row is partially filled
      return (
        isAccessRulePartiallyFilled(rule) &&
        (!rule.service || !rule.path || rule.methods.length === 0)
      )
    })
  }, [params.accessRules, isAccessRulePartiallyFilled])

  const submitForm = useCallback(async () => {
    // Mark form as submitted to show validation errors
    setHasFormBeenSubmitted(true)

    // Prevent submission if there are incomplete access rules
    // Note: Roles validation is handled by the form context
    if (hasIncompleteAccessRules) {
      return
    }

    const expiresAt = params.expirationDate || undefined

    // Filter valid access rules and expand each rule with multiple methods into separate rules
    // OpenStack API requires one method per access rule, so we expand multi-select methods
    const validAccessRules = params.accessRules
      .filter((rule) => rule.service && rule.path && rule.methods.length > 0)
      .flatMap((rule) =>
        rule.methods.map((method) => ({
          service: rule.service,
          path: rule.path,
          method: method,
        })),
      )
    const createParams = {
      name: params.name,
      ...(params.description && { description: params.description }),
      ...(expiresAt && { expires_at: expiresAt }),
      ...(params.roles.length > 0 && { roles: params.roles.map((role) => ({ id: role.id })) }),
      unrestricted: !params.restricted,
      // access_rules and unrestricted are independent - send access_rules if any are defined
      ...(validAccessRules.length > 0 && { access_rules: validAccessRules }),
    }

    const { success, response } = await update(createParams)
    if (success && response) {
      setCreatedCredential(response as ApplicationCredential)
      setShowSuccessModal(true)
    }
  }, [params, update, hasIncompleteAccessRules])

  const handleClose = useCallback(() => {
    setParams(defaultParams)
    setHasFormBeenSubmitted(false)
    reset()
    history.push(routeManager.routes.openstack.applicationCredentials.path())
  }, [history, reset, setParams])

  const handleSuccessClose = useCallback(() => {
    setShowSuccessModal(false)
    setCreatedCredential(null)
    onSuccess?.()
    handleClose()
  }, [handleClose, onSuccess])

  const handleRolesChange = useCallback(
    (roles: RoleOption[]) => {
      updateParams({ roles })
    },
    [updateParams],
  )

  if (showSuccessModal && createdCredential) {
    return <SuccessModal credential={createdCredential} onClose={handleSuccessClose} />
  }

  return (
    <ModalForm
      route={addRoute}
      title="Create Application Credentials"
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating}
      error={error}
      submitTitle="Create Application Credentials"
      maxWidth={800}
      showConfirmationOnCancel={true}
    >
      <div className={classes.formGrid}>
        <TextField
          id="name"
          label="Name"
          onChange={getParamsUpdater('name')}
          value={params.name}
          required
          placeholder="Enter Name"
          maxLength={255}
        />
        <div className={classes.dropdownWrapper}>
          <ValidatedRolesMultiPicklist
            id="roles"
            onChange={handleRolesChange}
            value={params.roles}
            required
          />
        </div>
        <TextField
          id="description"
          label="Description"
          onChange={getParamsUpdater('description')}
          value={params.description}
          placeholder="Enter Description"
        />
        <DateTimePickerField
          id="expiration"
          label="Expiration"
          onChange={getParamsUpdater('expirationDate')}
          value={params.expirationDate}
          placeholder="Select date"
          minDate={new Date()}
          required
        />
        <div className={classes.fullWidth}>
          <div className={classes.restrictedContainer}>
            <ToggleSwitch
              active={params.restricted || false}
              onClick={(active) => getParamsUpdater('restricted')(active)}
              label="Restricted"
            />
            <HintTooltip message="When enabled, this credential cannot create new credentials or trusts. Access rules will also be enforced if defined." />
          </div>
        </div>
      </div>

      <Text variant="subtitle2" style={{ marginTop: 24, marginBottom: 16 }}>
        Access Rules
      </Text>
      <div className={classes.accessRulesContainer}>
        {params.accessRules.map((rule, index) => (
          <div key={index} className={classes.accessRuleRow}>
            <PicklistField
              DropdownComponent={ServicePicklist}
              id={`service-${index}`}
              onChange={(value: string) => handleAccessRuleChange(index, 'service', value)}
              value={rule.service}
              label="Service"
              error={getAccessRuleFieldError(rule, 'service')}
            />
            <Combobox
              id={`path-${index}`}
              label="Path"
              items={getPathsForServiceMemo(rule.service).map((p) => p.path)}
              value={rule.path}
              onChange={(value: string) => handleAccessRuleChange(index, 'path', value)}
              placeholder="e.g. /v2.1/servers"
              disabled={!rule.service}
              allowCreate
              errorMessage={getAccessRuleFieldError(rule, 'path')}
            />
            <div className={classes.methodsWrapper}>
              <ValidatedMethodsMultiPicklist
                id={`methods-${index}`}
                onChange={(values: AccessRuleMethod[]) =>
                  handleAccessRuleChange(index, 'methods', values)
                }
                value={rule.methods}
                error={getAccessRuleFieldError(rule, 'methods')}
              />
            </div>
            <div className={classes.removeButtonWrapper}>
              {index > 0 && <RemoveButton index={index} onRemove={handleRemoveAccessRule} />}
            </div>
          </div>
        ))}
        <div className={classes.addButtonContainer} onClick={handleAddAccessRule}>
          <div className={classes.iconButton}>
            <FontAwesomeIcon solid className={classes.icon}>
              plus
            </FontAwesomeIcon>
          </div>
          <Text variant="body2">Add Access Rule</Text>
        </div>
      </div>
    </ModalForm>
  )
}
